import { FC, useState } from 'react';
import { useStore } from '@nanostores/react';
import {
  $players,
  $managePlayersModalIsOpen,
  addPlayer,
  updatePlayer,
  removePlayer,
  Player,
} from '../store/planner';
import {
  ModalOverlay,
  ModalContainer,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalContent,
  ModalFooter,
  CancelButton,
  FormContainer,
  FormControl,
  FieldLabel,
  PlayerNumberInput,
} from './PlanFormPage.style';
import {
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { Button as AppButton } from '../components/Button';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import {
  white,
  yellow,
  darkBlueBackground,
  darkBlueBorder,
} from '../components/colors';
import { Button, Modal, Select } from 'antd';
import { TextInput } from '../components/TextInput';

// --- Styled Components ---

const PlayersListContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

const PlayerRow = styled.div<{ $isEditing?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background-color: ${(props) =>
    props.$isEditing ? darkBlueBackground : darkBlueBorder};
  border: 2px solid
    ${(props) => (props.$isEditing ? yellow : 'rgba(255, 255, 255, 0.2)')};
  border-radius: 8px;
  transition: border-color 0.2s;

  &:hover {
    border-color: ${(props) =>
      props.$isEditing ? yellow : 'rgba(255, 255, 255, 0.4)'};
  }
`;

const PlayerNumberBadge = styled.div`
  background-color: ${yellow};
  color: #000;
  font-weight: bold;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  flex-shrink: 0;
`;

const PlayerDetailsColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const PlayerNameText = styled.div`
  font-weight: bold;
  font-size: 16px;
  color: ${white};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PlayerFlagText = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.6);
  text-transform: uppercase;
  font-weight: bold;
`;

const ActionButton = styled(Button)`
  &.ant-btn {
    background-color: transparent;
    border: 1px solid rgba(255, 255, 255, 0.3);
    color: ${white};
    font-weight: bold;
    width: 40px;
    height: 40px;
    padding: 0;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background-color: rgba(255, 255, 255, 0.1) !important;
      border-color: ${white} !important;
      color: ${white} !important;
    }
  }
`;

const ActionButtonDanger = styled(ActionButton)`
  &.ant-btn {
    border-color: #ef5350;
    color: #ef5350;

    &:hover {
      background-color: rgba(239, 83, 80, 0.15) !important;
      border-color: #ef5350 !important;
      color: #ef5350 !important;
    }
  }
`;

const ActionButtonSuccess = styled(ActionButton)`
  &.ant-btn {
    border-color: #66bb6a;
    color: #66bb6a;

    &:hover {
      background-color: rgba(102, 187, 106, 0.15) !important;
      border-color: #66bb6a !important;
      color: #66bb6a !important;
    }
  }
`;

const AddPlayerForm = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-end;
  width: 100%;
  padding: 16px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 8px;
`;

const AddPlayerFormInputs = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: flex-end;
`;

const PositionSelect = styled(Select)`
  &.ant-select {
    height: 56px;
    min-width: 130px;
  }

  .ant-select-selector {
    background-color: ${darkBlueBorder} !important;
    color: ${white} !important;
    border-color: ${white} !important;
    border-width: 2px !important;
    border-radius: 8px !important;
    padding: 8px !important;
    min-height: 56px !important;
    font-weight: bold;
    font-size: 16px;
  }

  .ant-select-selection-item {
    color: ${white} !important;
    font-weight: bold;
  }

  .ant-select-arrow {
    color: ${white};
  }

  &.ant-select-focused .ant-select-selector {
    border-color: ${yellow} !important;
    box-shadow: none !important;
  }
` as typeof Select;

const EditInputRow = styled.div`
  flex: 1;
  display: flex;
  gap: 12px;
  align-items: center;
`;

const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-bottom: 8px;
`;

const PlayerCount = styled.span`
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  font-weight: bold;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 48px 24px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 16px;
`;

// --- Component ---

export const ManagePlayersModal: FC = () => {
  const isOpen = useStore($managePlayersModalIsOpen);
  const players = useStore($players);

  // Add form state
  const [newName, setNewName] = useState('');
  const [newNumber, setNewNumber] = useState<number | null>(null);
  const [newIsGoalkeeper, setNewIsGoalkeeper] = useState(false);

  // Edit state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNumber, setEditNumber] = useState<number | null>(null);
  const [editIsGoalkeeper, setEditIsGoalkeeper] = useState(false);

  // Delete confirmation
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleClose = () => {
    $managePlayersModalIsOpen.set(false);
    resetAddForm();
    setEditingId(null);
  };

  const resetAddForm = () => {
    setNewName('');
    setNewNumber(null);
    setNewIsGoalkeeper(false);
  };

  const handleAdd = () => {
    if (!newName.trim() || newNumber === null) return;

    addPlayer({
      id: uuidv4(),
      name: newName.trim(),
      number: newNumber,
      flag: newIsGoalkeeper ? 'goalkeeper' : 'field',
    });
    resetAddForm();
  };

  const handleStartEdit = (player: Player) => {
    setEditingId(player.id);
    setEditName(player.name);
    setEditNumber(player.number);
    setEditIsGoalkeeper(player.flag === 'goalkeeper');
  };

  const handleSaveEdit = () => {
    if (!editingId || !editName.trim() || editNumber === null) return;

    updatePlayer({
      id: editingId,
      name: editName.trim(),
      number: editNumber,
      flag: editIsGoalkeeper ? 'goalkeeper' : 'field',
    });
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteConfirm = (id: string) => {
    setDeleteConfirmId(id);
  };

  const handleDeleteExecute = () => {
    if (deleteConfirmId) {
      removePlayer(deleteConfirmId);
      setDeleteConfirmId(null);
      if (editingId === deleteConfirmId) {
        setEditingId(null);
      }
    }
  };

  // Sort: goalkeepers first, then by number
  const sortedPlayers = [...players].sort((a, b) => {
    if (a.flag === 'goalkeeper' && b.flag !== 'goalkeeper') return -1;
    if (a.flag !== 'goalkeeper' && b.flag === 'goalkeeper') return 1;
    return a.number - b.number;
  });

  const deleteTargetPlayer = players.find((p) => p.id === deleteConfirmId);

  return (
    <>
      <ModalOverlay $isOpen={isOpen}>
        <ModalContainer>
          <ModalHeader>
            <ModalTitle>Manage Players</ModalTitle>
            <CloseButton onClick={handleClose}>
              <div>
                <XMarkIcon
                  style={{
                    color: '#fff',
                    height: '30px',
                    width: '30px',
                  }}
                  fontSize={30}
                />
              </div>
            </CloseButton>
          </ModalHeader>

          <ModalContent>
            <FormContainer>
              {/* Add new player form */}
              <FormControl>
                <FieldLabel>Add New Player</FieldLabel>
                <AddPlayerForm>
                  <AddPlayerFormInputs>
                    <div style={{ flex: 1 }}>
                      <TextInput
                        disableForceUppercase={true}
                        label="Player Name"
                        placeholder="Enter player name..."
                        value={newName}
                        onChange={setNewName}
                      />
                    </div>
                    <PlayerNumberInput
                      min={1}
                      max={14}
                      value={newNumber}
                      onChange={(value) => setNewNumber(value as number | null)}
                      placeholder="#"
                    />
                    <PositionSelect
                      value={newIsGoalkeeper ? 'goalkeeper' : 'field'}
                      onChange={(value) =>
                        setNewIsGoalkeeper(value === 'goalkeeper')
                      }
                      options={[
                        { value: 'field', label: 'Field' },
                        { value: 'goalkeeper', label: 'Goalkeeper' },
                      ]}
                      popupMatchSelectWidth={false}
                    />
                  </AddPlayerFormInputs>
                  <AppButton
                    style={{ margin: 0, height: '56px', fontSize: '16px' }}
                    large={true}
                    onClick={handleAdd}
                  >
                    Add
                  </AppButton>
                </AddPlayerForm>
              </FormControl>

              {/* Player list */}
              <FormControl>
                <SectionHeader>
                  <FieldLabel>Player Roster</FieldLabel>
                  <PlayerCount>{players.length} players</PlayerCount>
                </SectionHeader>

                {sortedPlayers.length === 0 ? (
                  <EmptyState>
                    No players added yet. Use the form above to add players.
                  </EmptyState>
                ) : (
                  <PlayersListContainer>
                    {sortedPlayers.map((player) => (
                      <PlayerRow
                        key={player.id}
                        $isEditing={editingId === player.id}
                      >
                        {editingId === player.id ? (
                          // Edit mode
                          <>
                            <EditInputRow>
                              <div style={{ flex: 1 }}>
                                <TextInput
                                  label=""
                                  placeholder="Player name..."
                                  value={editName}
                                  onChange={setEditName}
                                />
                              </div>
                              <PlayerNumberInput
                                min={1}
                                max={14}
                                value={editNumber}
                                onChange={(value) =>
                                  setEditNumber(value as number | null)
                                }
                                placeholder="#"
                              />
                              <PositionSelect
                                value={
                                  editIsGoalkeeper ? 'goalkeeper' : 'field'
                                }
                                onChange={(value) =>
                                  setEditIsGoalkeeper(value === 'goalkeeper')
                                }
                                options={[
                                  { value: 'field', label: 'Field' },
                                  { value: 'goalkeeper', label: 'Goalkeeper' },
                                ]}
                                popupMatchSelectWidth={false}
                              />
                            </EditInputRow>
                            <ActionButtonSuccess onClick={handleSaveEdit}>
                              <CheckIcon style={{ width: 20, height: 20 }} />
                            </ActionButtonSuccess>
                            <ActionButton onClick={handleCancelEdit}>
                              <XCircleIcon style={{ width: 20, height: 20 }} />
                            </ActionButton>
                          </>
                        ) : (
                          // View mode
                          <>
                            <PlayerNumberBadge>
                              {player.number}
                            </PlayerNumberBadge>
                            <PlayerDetailsColumn>
                              <PlayerNameText>{player.name}</PlayerNameText>
                              <PlayerFlagText>{player.flag}</PlayerFlagText>
                            </PlayerDetailsColumn>
                            <ActionButton
                              onClick={() => handleStartEdit(player)}
                            >
                              <PencilIcon style={{ width: 18, height: 18 }} />
                            </ActionButton>
                            <ActionButtonDanger
                              onClick={() => handleDeleteConfirm(player.id)}
                            >
                              <TrashIcon style={{ width: 18, height: 18 }} />
                            </ActionButtonDanger>
                          </>
                        )}
                      </PlayerRow>
                    ))}
                  </PlayersListContainer>
                )}
              </FormControl>
            </FormContainer>
          </ModalContent>

          <ModalFooter>
            <div style={{ flex: 1 }}></div>
            <CancelButton onClick={handleClose}>Close</CancelButton>
          </ModalFooter>
        </ModalContainer>
      </ModalOverlay>

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Player"
        open={deleteConfirmId !== null}
        onOk={handleDeleteExecute}
        onCancel={() => setDeleteConfirmId(null)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{
          danger: true,
          style: { fontWeight: 'bold' },
        }}
      >
        <p>
          Are you sure you want to delete{' '}
          <strong>
            {deleteTargetPlayer
              ? `${deleteTargetPlayer.name} (#${deleteTargetPlayer.number})`
              : 'this player'}
          </strong>
          ?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>
    </>
  );
};
